import { CssBaseline, ThemeProvider } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import * as React from 'react';
import { Helmet } from 'react-helmet';
import { connect, useSelector } from 'react-redux';
import { Route, Switch } from 'react-router';
import { Action } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import {
	ProtectedRoute,
	ProtectedRouteProps,
} from './components/ProtectedRoute/ProtectedRoute';
import {
	ROUTE_CONTRACT_WIZARD,
	ROUTE_HOME,
	ROUTE_LOGIN,
	ROUTE_LOGOUT,
	ROUTE_THANKYOU,
} from './lib/constants';

// pages
import ContractWizard from './pages/ContractWizard';
import Home from './pages/Home';
import { Login } from './pages/Login/Login';
import { Logout } from './pages/Logout/Logout';
import { ThankYou } from './pages/ThankYou/ThankYou';
import { Playground } from './pages/Playground';
import { VendorTest } from './pages/VendorTest';
import { tokenLogin } from './redux/actions/mobivers';
import { mobiversTheme } from './theme/theme';
import {AppState, CustomizingState, UserState} from './types/redux';
import { deleteCookie, getCookie } from './utils/cookie';
import { log } from './utils/logger';
// import cookieBotStyles from 'vendor/cookiebot/cookieBotStyles';
import { getCookieValue } from 'lib/getCookieValue';
import { DmsApiData } from 'redux/reducers/DmsApiData';

interface AppProps extends StateProps, DispatchProps {}

interface StateProps {
	user: UserState;
}

const mapStateToProps = (state: AppState) => ({
	user: state.user,
});

interface DispatchProps {
	tokenLogin(refreshToken: string): void;
}

/* istanbul ignore next */
const mapDispatchToProps = (
	dispatch: ThunkDispatch<Promise<any>, void, Action>
) => ({
	tokenLogin: (refreshToken: string) => {
		dispatch(tokenLogin(refreshToken));
	},
});

const useGlobalStyles = makeStyles((theme) => ({
	'@global': {
		body: {
			fontFamily: `'din-2014', sans-serif`,
			backgroundColor: theme.palette.grey[100],
		},
		// todo: this is used in some cases to open a modal window, as a refactoring task: this could be a common component
		button: {
			border: 0,
			background: 'none',
			padding: 0,
			color: theme.palette.primary.main,
			cursor: 'pointer',
			textDecoration: 'underline',
			fontSize: 'inherit',
			fontFamily: 'inherit',
		},

		a: {
			textDecoration: 'none',
			color: theme.palette.primary.main,
		},
	},
}));

// const useCookieBotStyles = makeStyles(cookieBotStyles);

const InternalApp: React.FC<AppProps> = (props) => {
	// global styles
	useGlobalStyles();
	// vendor styles
	// useCookieBotStyles();
	// check if SSO cookie has been set for login via token
	React.useEffect(
		() => {
			const ssoCookie = getCookie('_msso');
			if (ssoCookie) {
				try {
					// extract token
					const data = decodeURIComponent(ssoCookie);
					const json = data ? JSON.parse(data) : {};
					log('sso cookie detected', json);
					if (json.token) {
						// perform login via token
						props.tokenLogin(json.token);
					}
				} catch (e) {
					log('error parsing sso cookie');
				} finally {
					// always delete cookie after attempted login
					deleteCookie('_msso');
				}
			}

			// dms auto login
			const dmsDataString = getCookieValue('dms-data');
			if (dmsDataString) {
				const dmsData: DmsApiData = JSON.parse(dmsDataString);
				props.tokenLogin(dmsData.refreshToken);
			}
		},
		// eslint-disable-next-line
		[]
	);

	// todo: refactor props to protected route component
	const protectedRouteProps: ProtectedRouteProps = {
		isAuthenticated: !!props.user.access_token,
		authenticationPath: `/${ROUTE_LOGIN}`,
	};

	const customizing: CustomizingState = useSelector<AppState, CustomizingState>( (state) => state.customizing );
	let getTitle = customizing?.name==="bank11"? "Bank11":"MOBIVERS";
	return (
		<ThemeProvider theme={mobiversTheme(customizing)}>
			<CssBaseline />
			<div className="mobiversApp">
				<Helmet>
					<title>{getTitle}</title>
					<meta
						name="application-name"
						content={getTitle}
						data-npm-version={process.env.REACT_APP_NPM_VERSION}
						data-build-version={process.env.REACT_APP_VERSION}
					/>
					<meta
						httpEquiv="Content-Security-Policy"
						content="upgrade-insecure-requests"
					/>
				</Helmet>
				<Switch>
					<Route path={`/${ROUTE_LOGIN}`} component={Login} />
					<Route path={`/${ROUTE_LOGOUT}`} component={Logout} />
					<ProtectedRoute
						{...protectedRouteProps}
						exact={true}
						path={`/${ROUTE_HOME}`}
						component={Home}
					/>
					<ProtectedRoute
						{...protectedRouteProps}
						exact={true}
						path={`/${ROUTE_CONTRACT_WIZARD}/:salesType`}
						component={ContractWizard}
					/>
					<ProtectedRoute
						{...protectedRouteProps}
						exact={true}
						path={`/${ROUTE_THANKYOU}/:salesType`}
						component={ThankYou}
					/>
					{/* {process.env.NODE_ENV === 'development' && (
						<Route exact path="/vendor-test" component={VendorTest} />
					)} */}
					<Route exact path="/vendor-test" component={VendorTest} />
					{process.env.NODE_ENV === 'development' && (
						<Route exact path="/playground" component={Playground} />
					)}
					<Route component={Login} />
				</Switch>
			</div>
		</ThemeProvider>
	);
};

export const App = connect(mapStateToProps, mapDispatchToProps)(InternalApp);
